import { Vector3 } from 'three';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import type { VisualNode } from '../projection';
import type { CoreLayout } from './coreLayout';

type Ring = { parentPath: string; center: Vector3Tuple; rotation: Vector3Tuple; nodes: VisualNode[] };
type Segment = { from: Vector3; to: Vector3 };
type Conflict = { description: string; depth: number };
const GUIDE_CLEARANCE = 0.11;
const CIRCLE_SEGMENTS = 96;
const READABILITY_GAP_MULTIPLIER = 3;

function segmentDistanceSquared(a: Segment, b: Segment): number {
    const u = a.to.clone().sub(a.from), v = b.to.clone().sub(b.from), w = b.from.clone().sub(a.from);
    const aa = u.lengthSq(), cc = v.lengthSq();
    const pointDistance = (point: Vector3, segment: Segment, direction: Vector3, length: number) =>
        point.distanceToSquared(segment.from.clone().addScaledVector(direction,
            length === 0 ? 0 : Math.max(0, Math.min(1, point.clone().sub(segment.from).dot(direction) / length))));
    let distance = Math.min(pointDistance(a.from, b, v, cc), pointDistance(a.to, b, v, cc),
        pointDistance(b.from, a, u, aa), pointDistance(b.to, a, u, aa));
    const normal = u.clone().cross(v);
    const determinant = normal.lengthSq();
    if (determinant > 0) {
        const s = w.clone().cross(v).dot(normal) / determinant, t = w.clone().cross(u).dot(normal) / determinant;
        if (s >= 0 && s <= 1 && t >= 0 && t <= 1) {
            distance = Math.min(distance, a.from.clone().addScaledVector(u, s)
                .distanceToSquared(b.from.clone().addScaledVector(v, t)));
        }
    }
    return distance;
}

function clearanceConflict(layout: CoreLayout, rings: Ring[]): Conflict | undefined {
    const depthByPath = new Map(rings.flatMap(ring => ring.nodes.map(node => [node.path, node.depth] as const)));
    depthByPath.set('.', 0);
    const conflict = (description: string, ...depths: number[]): Conflict =>
        ({ description, depth: Math.max(...depths) });
    const entries = [...layout.entryByPath];
    const circles = rings.map(ring => {
        const radius = layout.ringRadiusByPath.get(ring.parentPath)!;
        const center = new Vector3(...ring.center);
        const points = Array.from({ length: CIRCLE_SEGMENTS }, (_, i) =>
            center.clone().add(new Vector3(radius * Math.cos(i * 2 * Math.PI / CIRCLE_SEGMENTS),
                radius * Math.sin(i * 2 * Math.PI / CIRCLE_SEGMENTS), 0)));
        return { ring, radius, center, sagitta: radius * (1 - Math.cos(Math.PI / CIRCLE_SEGMENTS)),
            segments: points.map((from, i) => ({ from, to: points[(i + 1) % points.length]! })) };
    });
    const spokes = rings.map(ring => ({ ring,
        from: new Vector3(...layout.entryByPath.get(ring.parentPath)!.position), to: new Vector3(...ring.center) }));
    for (let i = 0; i < circles.length; i++) {
        const a = circles[i]!;
        for (let j = i + 1; j < circles.length; j++) {
            const b = circles[j]!;
            const lateral = Math.hypot(a.center.x - b.center.x, a.center.y - b.center.y);
            const planar = Math.max(0, lateral - a.radius - b.radius, Math.abs(a.radius - b.radius) - lateral);
            if (Math.hypot(planar, a.center.z - b.center.z) <= 2 * GUIDE_CLEARANCE) {
                return conflict(`rings ${a.ring.parentPath} and ${b.ring.parentPath}`,
                    a.ring.nodes[0]!.depth, b.ring.nodes[0]!.depth);
            }
        }
    }
    for (let i = 0; i < entries.length; i++) {
        const [path, entry] = entries[i]!;
        const point = new Vector3(...entry.position);
        for (let j = i + 1; j < entries.length; j++) {
            const [otherPath, other] = entries[j]!;
            if (Math.abs(entry.position[2] - other.position[2]) > entry.visualRadius + other.visualRadius) continue;
            if (point.distanceTo(new Vector3(...other.position)) <= entry.visualRadius + other.visualRadius) {
                return conflict(`nodes ${path} and ${otherPath}`, depthByPath.get(path)!, depthByPath.get(otherPath)!);
            }
        }
        for (const circle of circles) {
            if (Math.abs(point.z - circle.center.z) > entry.visualRadius + GUIDE_CLEARANCE) continue;
            if (circle.ring.nodes.some(node => node.path === path)) continue;
            const offset = point.clone().sub(circle.center);
            if (Math.hypot(Math.hypot(offset.x, offset.y) - circle.radius, offset.z)
                <= entry.visualRadius + GUIDE_CLEARANCE) return conflict(`node ${path} and ring ${circle.ring.parentPath}`,
                    depthByPath.get(path)!, circle.ring.nodes[0]!.depth);
        }
    }
    for (let i = 0; i < spokes.length; i++) {
        const spoke = spokes[i]!;
        for (const [path, entry] of entries) {
            if (path === spoke.ring.parentPath) continue;
            if (entry.position[2] + entry.visualRadius + GUIDE_CLEARANCE < spoke.to.z
                || entry.position[2] - entry.visualRadius - GUIDE_CLEARANCE > spoke.from.z) continue;
            const point = new Vector3(...entry.position);
            if (segmentDistanceSquared(spoke, { from: point, to: point })
                <= (entry.visualRadius + GUIDE_CLEARANCE) ** 2) return conflict(`spoke ${spoke.ring.parentPath} and node ${path}`,
                    spoke.ring.nodes[0]!.depth, depthByPath.get(path)!);
        }
        for (const circle of circles) {
            if (Math.max(spoke.from.z, spoke.to.z) + GUIDE_CLEARANCE + circle.sagitta < circle.center.z
                || Math.min(spoke.from.z, spoke.to.z) - GUIDE_CLEARANCE - circle.sagitta > circle.center.z) continue;
            if (segmentDistanceSquared(spoke, { from: circle.center, to: circle.center })
                > (circle.radius + GUIDE_CLEARANCE + circle.sagitta) ** 2) continue;
            let from = spoke.from;
            if (circle.ring.nodes.some(node => node.path === spoke.ring.parentPath)) {
                // Exempt the node/guide joint, not the rest of the departure.
                const radius = layout.entryByPath.get(spoke.ring.parentPath)!.visualRadius;
                const attachment = Math.max(radius, 2 * (GUIDE_CLEARANCE + circle.sagitta));
                from = from.clone().lerp(spoke.to, Math.min(1, attachment / from.distanceTo(spoke.to)));
            }
            if (circle.segments.some(segment => segmentDistanceSquared({ from, to: spoke.to }, segment)
                <= (GUIDE_CLEARANCE + circle.sagitta) ** 2)) {
                return conflict(`spoke ${spoke.ring.parentPath} and ring ${circle.ring.parentPath}`,
                    spoke.ring.nodes[0]!.depth, circle.ring.nodes[0]!.depth);
            }
        }
        for (let j = i + 1; j < spokes.length; j++) {
            if (spoke.to.z - GUIDE_CLEARANCE > spokes[j]!.from.z
                || spokes[j]!.to.z - GUIDE_CLEARANCE > spoke.from.z) continue;
            if (segmentDistanceSquared(spoke, spokes[j]!) <= GUIDE_CLEARANCE ** 2) {
                return conflict(`spokes ${spoke.ring.parentPath} and ${spokes[j]!.ring.parentPath}`,
                    spoke.ring.nodes[0]!.depth, spokes[j]!.ring.nodes[0]!.depth);
            }
        }
    }
    return undefined;
}

export function compactStandingLayout(layout: CoreLayout, rings: Ring[], radialLength: number, gap: number) {
    const ringByNode = new Map(rings.flatMap(ring => ring.nodes.map(node => [node.path, ring] as const)));
    const layers = new Map<number, Ring[]>();
    for (const ring of rings) {
        const depth = ring.nodes[0]!.depth;
        const layer = layers.get(depth) ?? [];
        layer.push(ring);
        layers.set(depth, layer);
    }
    const minimumStep = Math.max(radialLength, 2 * GUIDE_CLEARANCE);
    const depthSteps = new Map([...layers.keys()].map(depth => [depth, minimumStep]));
    let lastConflict = '';
    for (let attempt = 0; attempt <= layers.size * 8; attempt++) {
        let z = 0;
        for (const [depth, layer] of layers) {
            z -= depthSteps.get(depth)!;
            const placed: { start: Vector3; center: Vector3; radius: number }[] = [];
            const groups = new Map<Ring | undefined, Ring[]>();
            for (const ring of layer) {
                const owner = ringByNode.get(ring.parentPath);
                const siblings = groups.get(owner) ?? [];
                siblings.push(ring);
                groups.set(owner, siblings);
            }
            const sorted = [...groups].sort(([a], [b]) => {
                const startA = a?.center ?? [0, 0, 0], startB = b?.center ?? [0, 0, 0];
                return Math.hypot(startA[0]!, startA[1]!) - Math.hypot(startB[0]!, startB[1]!)
                    || ((a?.parentPath ?? '') < (b?.parentPath ?? '') ? -1 : 1);
            });
            for (const [owner, siblings] of sorted) {
                const start = new Vector3(...(owner?.center ?? [0, 0, 0]));
                start.z = 0;
                const direction = owner ? start.clone().sub(new Vector3(...layout.entryByPath.get(owner.parentPath)!.position)) : new Vector3();
                direction.z = 0;
                direction.normalize();
                if (direction.lengthSq() === 0) direction.set(1, 0, 0);
                const members = siblings.map(ring => {
                    const source = new Vector3(...layout.entryByPath.get(ring.parentPath)!.position);
                    source.z = 0;
                    const radius = layout.ringRadiusByPath.get(ring.parentPath)!;
                    return { ring, source, radius, envelope: radius
                        + Math.max(...ring.nodes.map(node => layout.entryByPath.get(node.path)!.visualRadius)) };
                });
                let expansion = owner?.parentPath === '.'
                    ? 1 + radialLength / layout.ringRadiusByPath.get('.')! : 1;
                for (let i = 0; i < members.length; i++) {
                    for (let j = i + 1; j < members.length; j++) {
                        const a = members[i]!, b = members[j]!;
                        expansion = Math.max(expansion, (a.envelope + b.envelope + gap * READABILITY_GAP_MULTIPLIER)
                            / a.source.distanceTo(b.source));
                    }
                }
                const envelope = Math.max(...members.map(member => member.source.distanceTo(start) * expansion + member.envelope));
                // A translated, uniformly expanded copy of the source ring gives siblings a coherent fan.
                const fanSpread = Math.max(...members.map(member => member.source.distanceTo(start) * (expansion - 1)));
                let distance = owner?.parentPath === '.' || !owner ? 0 : Math.max(radialLength, fanSpread * 1.5);
                const intervals: { from: number; to: number }[] = [];
                for (const other of placed) {
                    const delta = start.clone().sub(other.start);
                    const denominator = delta.dot(direction);
                    // Keep sibling spokes from converging as they leave their source plane.
                    if (denominator > 0) distance = Math.max(distance,
                        delta.dot(other.center.clone().sub(other.start)) / denominator);
                    const offset = start.clone().sub(other.center);
                    const along = offset.dot(direction);
                    const discriminant = (envelope + other.radius + gap * READABILITY_GAP_MULTIPLIER) ** 2 - (offset.lengthSq() - along * along);
                    if (discriminant >= 0) {
                        const half = Math.sqrt(discriminant);
                        intervals.push({ from: -along - half, to: -along + half });
                    }
                }
                intervals.sort((a, b) => a.from - b.from);
                for (const interval of intervals) {
                    if (distance >= interval.from && distance <= interval.to) distance = interval.to + gap * 1e-6;
                }
                const groupCenter = start.clone().addScaledVector(direction, distance);
                placed.push({ start, center: groupCenter.clone(), radius: envelope });
                for (const { ring, source, radius } of members) {
                    const center = source.clone().sub(start).multiplyScalar(expansion).add(groupCenter);
                    center.z = z;
                    ring.center = [center.x, center.y, center.z];
                    layout.ringCenterByPath.set(ring.parentPath, ring.center);
                    const outgoing = center.clone().sub(source);
                    const directoryCount = ring.nodes.filter(node => node.nodeKind === 'directory').length;
                    const phase = ring.parentPath === '.' ? 0
                        : Math.atan2(outgoing.y, outgoing.x) + Math.max(0, directoryCount - 1) * Math.PI / ring.nodes.length;
                    ring.rotation = [Math.PI / 2, phase, 0];
                    ring.nodes.forEach(node => {
                        const entry = layout.entryByPath.get(node.path)!;
                        const angle = entry.angle - phase;
                        entry.position = [center.x + radius * Math.cos(angle),
                            center.y - radius * Math.sin(angle), center.z];
                        if (![...entry.position].every(Number.isFinite)
                            || center.length() * Number.EPSILON > entry.visualRadius * 1e-4) {
                            throw new Error(`Ring-tree layout exceeds numeric precision at ${node.path}.`);
                        }
                    });
                }
            }
        }
        const conflict = clearanceConflict(layout, rings);
        if (!conflict) {
            layout.boundsRadius = Math.max(...[...layout.entryByPath.values()].map(entry =>
                new Vector3(...entry.position).length() + entry.visualRadius),
            ...rings.map(ring => new Vector3(...ring.center).length() + layout.ringRadiusByPath.get(ring.parentPath)!));
            return;
        }
        lastConflict = conflict.description;
        const step = depthSteps.get(conflict.depth)!;
        if (step >= minimumStep * 128) break;
        depthSteps.set(conflict.depth, step * 2);
    }
    throw new Error(`Cannot place standing ring-tree rings with verified clearance: ${lastConflict}.`);
}
